import { useMemo, useState } from 'react';
import type { ObjectMetadata } from '../../../data/client';
import {
  pagedIds,
  useFields,
  useInboundRelations,
  useInfiniteRecordList,
} from '../../../data/hooks';
import { Button, GroupedForm, Icon, Skeleton } from '../../../ui';
import { CreateRecordDialog } from '../../records/CreateRecordDialog';
import { toggleKey, useLocalToggle } from '../useLocalToggle';
import { LinkPicker } from './LinkPicker';
import { RelatedRow } from './RelatedRow';
import {
  endpointFieldsOf,
  type RelatedSection,
  relatedFilter,
  relatedSecondaryFields,
  relatedSelect,
} from './sectionModel';

/**
 * ONE related section: a heading with a count, a few rows, and the action that adds
 * to it.
 *
 * A COMPONENT PER SECTION, not a loop over queries, and that is a hard requirement
 * rather than a style: a section owns four hooks and there is a dynamic number of
 * sections, so a loop in the parent would break the rules of hooks the first time an
 * admin adds a relation field.
 *
 * FIVE ROWS, then "See all N". A related section answers "what is attached to this record",
 * and the answer to that is a shape and a count, not a table; the way past five is ONE click
 * that asks for every row, rather than the five-at-a-time paging wave 3 round 2 removed.
 */
const RELATED_PAGE_SIZE = 5;

/**
 * RENAMED IN WAVE 3 ROUND 2 for the reason the stream row was: step 0's legacy check fails
 * an import whose specifier ends in `RelatedSection`, the wave-1 component this replaced,
 * and round 1 had rebuilt the part as a kit group box with a footer row while keeping the
 * old file name. `RelatedSection` survives as the TYPE in `sectionModel.ts`, which is the
 * model of one section rather than the rejected component.
 */
export interface RelatedGroupProps {
  readonly parent: ObjectMetadata;
  readonly recordId: string;
  readonly section: RelatedSection;
  readonly readOnly: boolean;
}

export function RelatedGroup({ parent, recordId, section, readOnly }: RelatedGroupProps) {
  const [open, setOpen] = useLocalToggle(toggleKey('related', parent.id, section.id), true);
  const [pickerOpen, setPickerOpen] = useState(false);
  /**
   * "SEE ALL N", AND IT REALLY SHOWS ALL N (wave 3 round 2; `spec/screens/record.md` section 2
   * item 6 asks for "up to five rows ... a trailing 'See all N' when more exist").
   *
   * It replaces "Show more (5 of 12 shown)", which paged five at a time inside a group box -
   * the shape of scrolling the owner rejected on this screen, one click at a time. Asking for
   * every row is ONE request rather than N/5 of them: the count is already the server's, so the
   * limit becomes that count and the query re-keys once. It is a deliberate click and not the
   * default, so the section at rest is still the five rows the spec asks for.
   *
   * The count is taken at the CLICK, where it is known: the control only renders once page 0
   * has arrived with the server's total beside it.
   */
  const [seeAllLimit, setSeeAllLimit] = useState<number | null>(null);
  /**
   * P1-4: the child section's Add opens a dialog and stays here, which is what its
   * JUNCTION sibling two rows down has always done. The word was the same, the position
   * was the same, and the behaviour was the opposite - one opened a picker, the other
   * minted a record and navigated away from the page you were reading.
   */
  const [createOpen, setCreateOpen] = useState(false);

  /** The CHILD's own fields: what a row shows under its title, and a junction's role. */
  const childFields = useFields(section.object.id);
  /**
   * A junction's endpoint fields are SYSTEM fields, so no `listFields` call can ever
   * return them. They are reachable exactly one way: ask the object on the far side
   * which fields point at IT, and the junction's other endpoint is one of the answers.
   */
  const farRelations = useInboundRelations(
    section.otherEndpointObjectId ?? '',
    section.kind === 'junction',
  );

  const secondary = useMemo(
    () => relatedSecondaryFields(section, childFields.data ?? []),
    [section, childFields.data],
  );
  const endpointFields = useMemo(
    () => (farRelations.data === undefined ? [] : endpointFieldsOf(section, farRelations.data)),
    [section, farRelations.data],
  );
  const select = useMemo(
    () =>
      relatedSelect(
        section,
        secondary,
        endpointFields.map((field) => field.id),
      ),
    [section, secondary, endpointFields],
  );

  const filter = useMemo(() => relatedFilter(section, recordId), [section, recordId]);

  /**
   * WHETHER THIS SECTION CAN QUERY AT ALL, as its own value rather than inline, because
   * three different states render from it.
   *
   * `open` is what keeps eight sections from being sixteen statements on mount (open
   * risk 3). The endpoint condition is why a junction section waits: querying before the
   * endpoint id is known would fetch a page, then re-key and fetch it again the moment
   * the endpoint arrived.
   */
  const resolvable =
    childFields.data !== undefined &&
    select.length > 0 &&
    (section.kind === 'child' || endpointFields.length > 0);
  const list = useInfiniteRecordList(section.object.id, {
    select,
    filter,
    limit: seeAllLimit ?? RELATED_PAGE_SIZE,
    withTotalCount: true,
    enabled: open && resolvable,
  });

  const ids = pagedIds(list.data) ?? [];
  /** The count is the SERVER's, over the whole filtered set - never the loaded page's. */
  const total = list.data?.pages[0]?.totalCount;

  /**
   * THE SECTION'S ACTION, AS THE GROUP'S FOOTER ROW (wave 3, R-B1).
   *
   * It was a small "+ Add" button up on the heading line, one per section, which is what
   * owner-37 and owner-38 were looking at: eight sections meant eight page controls sitting on
   * eight headings, and a heading is not a toolbar. Apple's list puts the add action in the
   * list's own footer row - full width, the accent as the only mark - and the verb names what
   * it adds instead of saying "Add" eight times over.
   * kit: Apple macOS 27 UI Kit.sketch / Lists/Light/Footer/Footer
   */
  const junction = section.kind === 'junction';
  const action =
    readOnly || !open ? undefined : (
      <button
        type="button"
        className="record-row record-group-action"
        onClick={() => (junction ? setPickerOpen(true) : setCreateOpen(true))}
        // The endpoint decides which object the picker searches, so a junction's action
        // cannot act before it is known.
        disabled={junction && endpointFields.length === 0}
      >
        <Icon name="plus" size="0.9rem" />
        {/*
          ONE VERB FOR BOTH KINDS, which is P1-4's own finding kept: a child's action and a
          junction's action look the same, sit in the same place and behave the same way
          (open something, stay on this record), so calling one of them "Link" would put the
          difference back on the surface - and "junction" is platform mechanics that never
          reaches a user's screen. What the label adds is WHAT gets added, which the spec's
          own wording asks for ("Add <object>...") and which eight sections saying "Add"
          could not say.
        */}
        <span>{`Add ${section.object.labelSingular}…`}</span>
      </button>
    );

  /**
   * EMPTY ONLY WHEN THE SERVER SAID SO: an open section that resolved, loaded without an error
   * and counted zero. A folded section asks nothing and keeps its box, and a load error keeps
   * the box that says what failed (H7: never show a failed load as an empty list).
   */
  const empty =
    open && resolvable && !list.isPending && !list.isError && ids.length === 0 && total === 0;
  const emptyAction = readOnly ? null : (
    <>
      {' '}
      {/* NAMED WITH ITS LIST (coordinator, 4 Oct; DESIGN.md D1 "two different things never share
          a name on a screen"): two empty lists of one object - deals as closer and as setter -
          would otherwise put two "Add Deal…" controls on one record. The visible words stay; the
          accessible name adds the list's heading, the way the library's "More, <subject>" does. */}
      <Button
        variant="text"
        aria-label={`Add ${section.object.labelSingular}…, ${section.title}`}
        onClick={() => (junction ? setPickerOpen(true) : setCreateOpen(true))}
        disabled={junction && endpointFields.length === 0}
      >
        {`Add ${section.object.labelSingular}…`}
      </Button>
    </>
  );

  return (
    /**
     * ONE GROUP BOX PER SECTION (wave 3, R-B1), which is the same part every field section on
     * this screen is: the kit's named disclosure over a wash-filled box of rows. It replaces
     * `CollapsibleSection`, a wave-1 primitive whose trigger tints on hover - the behaviour
     * rule this round takes off rows - and whose title is not the kit's group title.
     *
     * The GROUP BOX is also the landmark this section used to wrap itself in a `<section>` to
     * get: `GroupedForm` renders a real `<section>` with the title as its heading, so eight
     * sections are still eight things a screen reader can jump between.
     */
    <>
      <GroupedForm
        title={section.title}
        open={open}
        onOpenChange={setOpen}
        actions={
          total === undefined ? undefined : (
            <span className="text-2xs text-ink-subtle">{total}</span>
          )
        }
      >
        <>
          {!resolvable && !childFields.isPending && !farRelations.isPending ? (
            /**
             * REACHABLE, and it must not be a skeleton that spins forever. A junction
             * whose other endpoint sits on a HIDDEN object resolves nothing: the
             * inbound-relations read drops hidden objects, so the endpoint never arrives.
             * Saying what is missing beats an eternal placeholder, and it names the one
             * thing that fixes it.
             */
            <p className="record-row record-row-stacked text-sm text-ink-muted">
              This list cannot be shown while the object it reads is hidden. An admin can show it
              again under Configure.
            </p>
          ) : list.isPending ? (
            <div className="record-row record-row-stacked" aria-busy="true">
              <Skeleton className="h-5 w-48" label={`Loading ${section.title.toLowerCase()}`} />
              <Skeleton className="h-5 w-40" />
            </div>
          ) : list.isError ? (
            <p role="alert" className="record-row record-row-stacked text-sm text-danger">
              {`These ${section.object.labelPlural.toLowerCase()} could not be loaded. ${list.error.message}`}
            </p>
          ) : ids.length === 0 ? (
            /**
             * D25: ONE LINE, not a block. Eight `EmptyState` panels at `px-6 py-12`
             * would make a fresh agency page a column of apologies; `EmptyState` is for
             * a screen with nothing on it, and this is a section inside a full page.
             *
             * AND ONE ACTION, NOT TWO (audit P2-11). The line used to end in an "Add
             * call" link 40px below the header's own "Add" button, both calling the
             * identical `addChild`: one create, two labels, two positions. Since wave 3
             * the surviving one is the group's FOOTER ROW, directly under this line,
             * which is where a kit list puts it.
             *
             * DESIGN.md's "empty states teach: one sentence what this is, one action to
             * start" is still satisfied: the sentence is here, the action is the row
             * directly below it, inside the same box, in the reader's eye already.
             */
            /*
                BR-RECORD: AND THE ACTION IS IN THE SAME LINE. An empty list keeps its heading
                and is one muted row, the sentence and its action, where it was two - the
                sentence, then the footer row (DESIGN.md H4: "one muted line that says what
                belongs there ... with its inline action", N21; register A6-13). Same words.
              */
            <p className="record-row record-row-stacked text-sm text-ink-muted">
              <span>
                {`No ${section.object.labelPlural}`}
                {empty ? emptyAction : null}
              </span>
            </p>
          ) : (
            <ul>
              {ids.map((id) => (
                <RelatedRow
                  key={id}
                  section={section}
                  recordId={id}
                  secondary={secondary}
                  endpointFields={endpointFields}
                  readOnly={readOnly}
                />
              ))}
            </ul>
          )}

          {/* THE WAY OUT OF FIVE ROWS IS A FOOTER ROW TOO, above the add row: both are the
              list's own trailing rows and neither is a control on the heading. */}
          {seeAllLimit === null && list.hasNextPage === true && total !== undefined && (
            <button
              type="button"
              className="record-row record-group-action"
              disabled={list.isFetching}
              onClick={() => setSeeAllLimit(total)}
            >
              <Icon name="arrow-down" size="0.9rem" />
              <span>{`See all ${total}`}</span>
            </button>
          )}
          {empty ? null : action}
        </>
      </GroupedForm>

      {/**
       * THE CHILD CREATE, AND IT DOES NOT LEAVE (P1-4). The parent is prefilled and SHOWN -
       * the dialog names the relation and renders the parent's own title through the field
       * registry, so nobody has to trust that the link is being made. `onCreated` does
       * nothing on purpose: the write invalidates this object's lists, so the new child
       * arrives in the section that made it, which is where the person is looking.
       */}
      {section.kind === 'child' && (
        <CreateRecordDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          object={section.object}
          prefill={[{ field: section.field, value: recordId }]}
          onCreated={() => undefined}
        />
      )}

      {section.kind === 'junction' &&
        section.relationFieldId !== undefined &&
        section.otherEndpointObjectId !== undefined && (
          <LinkPicker
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            parentObjectId={parent.id}
            parentRecordId={recordId}
            relationFieldId={section.relationFieldId}
            targetObjectId={section.otherEndpointObjectId}
            parentIsSource={section.parentIsSource === true}
            sectionTitle={section.title}
          />
        )}
    </>
  );
}
